import React, { useState, useRef } from "react";
import { useToast } from "./Toast";

interface BugReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback after successful submission
}

type Severity = "low" | "medium" | "high" | "critical";
type Category = "ui" | "functionality" | "performance" | "data" | "other";

export default function BugReportModal({ isOpen, onClose, onSuccess }: BugReportModalProps) {
  const { showToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [showSuccess, setShowSuccess] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [stepsToReproduce, setStepsToReproduce] = useState("");
  const [expectedBehavior, setExpectedBehavior] = useState("");
  const [actualBehavior, setActualBehavior] = useState("");
  const [severity, setSeverity] = useState<Severity>("medium");
  const [category, setCategory] = useState<Category>("other");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  const resetForm = () => {
    setTitle("");
    setDescription("");
    setStepsToReproduce("");
    setExpectedBehavior("");
    setActualBehavior("");
    setSeverity("medium");
    setCategory("other");
    setScreenshot(null);
    setCurrentStep(1);
    setShowSuccess(false);
    setIsSubmitting(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    // Validation
    if (!title.trim() || !description.trim()) {
      showToast("Please fill in at least the title and description", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      // Gather browser/device info
      const url = window.location.href;
      const browser = navigator.userAgent;
      const device = /Mobile|Android|iPhone|iPad/.test(navigator.userAgent) ? "mobile" : "desktop";

      // Prepare form data
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      formData.append("stepsToReproduce", stepsToReproduce);
      formData.append("expectedBehavior", expectedBehavior);
      formData.append("actualBehavior", actualBehavior);
      formData.append("severity", severity);
      formData.append("category", category);
      formData.append("url", url);
      formData.append("browser", browser);
      formData.append("device", device);
      if (screenshot) {
        formData.append("screenshot", screenshot);
      }

      const response = await fetch("/bugs/report", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({} as any));
        // Upload rate limit: the server sends player-facing copy, use it as-is.
        throw new Error(err.message || "Failed to submit bug report. Please try again.");
      }

      // Show success state
      setShowSuccess(true);

      // Wait 2 seconds to show success animation, then close and refresh if needed
      setTimeout(() => {
        handleClose();
        if (onSuccess) {
          onSuccess(); // Call the callback to refresh data if on bugs page
        }
      }, 2000);
    } catch (error: any) {
      console.error("Error submitting bug report:", error);
      showToast(error?.message || "Failed to submit bug report. Please try again.", "error");
      setIsSubmitting(false);
    }
  };

  const severityColors = {
    low: { bg: "bg-blue-500/20", border: "border-blue-500/40", text: "text-blue-200" },
    medium: { bg: "bg-yellow-500/20", border: "border-yellow-500/40", text: "text-yellow-200" },
    high: { bg: "bg-orange-500/20", border: "border-orange-500/40", text: "text-orange-200" },
    critical: { bg: "bg-red-500/20", border: "border-red-500/40", text: "text-red-200" },
  };

  // Success screen
  if (showSuccess) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-green-500/30 bg-gradient-to-b from-[#0a0a0f] to-[#050508] shadow-2xl animate-in zoom-in-95 duration-200">
          <div className="p-8 text-center space-y-6">
            {/* Success checkmark animation */}
            <div className="mx-auto w-20 h-20 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center animate-in zoom-in duration-500">
              <svg className="w-10 h-10 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-white mb-2">Bug Report Submitted!</h3>
              <p className="text-sm text-white/60">Thank you for helping us improve the app.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-in fade-in duration-200">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-2xl border border-red-500/30 bg-gradient-to-b from-[#0a0a0f] to-[#050508] shadow-2xl animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="border-b border-white/10 bg-gradient-to-r from-red-500/20 via-red-600/10 to-transparent p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Report a Bug
              </h2>
              <p className="mt-2 text-sm text-white/60">
                Help us improve by reporting issues you encounter. Please be as detailed as possible.
              </p>
            </div>
            <button onClick={handleClose} className="rounded-lg p-2 hover:bg-white/10 transition-colors">
              <svg className="w-5 h-5 text-white/60" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mt-6">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div
                  className={`flex-1 h-2 rounded-full transition-all ${
                    step <= currentStep ? "bg-red-500" : "bg-white/10"
                  }`}
                />
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2 text-xs text-white/40">
            <span className={currentStep >= 1 ? "text-red-400 font-semibold" : ""}>Basic Info</span>
            <span className={currentStep >= 2 ? "text-red-400 font-semibold" : ""}>Details</span>
            <span className={currentStep >= 3 ? "text-red-400 font-semibold" : ""}>Classification</span>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-16rem)] p-6 space-y-6">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
              <div className="rounded-xl border border-cyan-500/20 bg-cyan-500/5 p-4">
                <h4 className="text-sm font-bold text-cyan-200 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Tips for a Great Bug Report
                </h4>
                <ul className="text-xs text-white/60 space-y-1 ml-6 list-disc">
                  <li>Use a clear, descriptive title</li>
                  <li>Describe what you were trying to do</li>
                  <li>Include any error messages you saw</li>
                  <li>Screenshots are extremely helpful!</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Bug Title <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., 'Cannot save character availability'"
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-red-500/50 focus:bg-white/10 transition-all"
                  maxLength={200}
                />
                <div className="mt-1 text-xs text-white/40">{title.length}/200 characters</div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Description <span className="text-red-400">*</span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What happened? Describe the issue you encountered..."
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-red-500/50 focus:bg-white/10 transition-all resize-none"
                  rows={5}
                  maxLength={2000}
                />
                <div className="mt-1 text-xs text-white/40">{description.length}/2000 characters</div>
              </div>
            </div>
          )}

          {/* Step 2: Details */}
          {currentStep === 2 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Steps to Reproduce
                </label>
                <textarea
                  value={stepsToReproduce}
                  onChange={(e) => setStepsToReproduce(e.target.value)}
                  placeholder="1. Go to the Roster page&#10;2. Click 'Add Character'&#10;3. Fill in character details&#10;4. Click Save..."
                  className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-red-500/50 focus:bg-white/10 transition-all resize-none"
                  rows={6}
                  maxLength={1000}
                />
                <div className="mt-1 text-xs text-white/40">Help us recreate the issue</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">
                    Expected Behavior
                  </label>
                  <textarea
                    value={expectedBehavior}
                    onChange={(e) => setExpectedBehavior(e.target.value)}
                    placeholder="What should have happened?"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-red-500/50 focus:bg-white/10 transition-all resize-none"
                    rows={4}
                    maxLength={500}
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-white/80 mb-2">
                    Actual Behavior
                  </label>
                  <textarea
                    value={actualBehavior}
                    onChange={(e) => setActualBehavior(e.target.value)}
                    placeholder="What actually happened?"
                    className="w-full rounded-lg border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-red-500/50 focus:bg-white/10 transition-all resize-none"
                    rows={4}
                    maxLength={500}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/80 mb-2">
                  Screenshot (Optional)
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => setScreenshot(e.target.files?.[0] || null)}
                  className="hidden"
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full rounded-lg border-2 border-dashed border-white/20 bg-white/5 px-4 py-8 hover:border-red-500/50 hover:bg-white/10 transition-all text-center"
                >
                  {screenshot ? (
                    <div className="space-y-2">
                      <div className="text-sm font-semibold text-green-400">✓ {screenshot.name}</div>
                      <div className="text-xs text-white/40">Click to change</div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <svg className="w-10 h-10 mx-auto text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <div className="text-sm text-white/60">Click to upload screenshot</div>
                      <div className="text-xs text-white/40">PNG, JPG up to 2MB</div>
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Classification */}
          {currentStep === 3 && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
              <div>
                <label className="block text-sm font-semibold text-white/80 mb-3">
                  Severity
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {(["low", "medium", "high", "critical"] as Severity[]).map((sev) => (
                    <button
                      key={sev}
                      onClick={() => setSeverity(sev)}
                      className={`rounded-lg border-2 p-4 transition-all ${
                        severity === sev
                          ? `${severityColors[sev].border} ${severityColors[sev].bg}`
                          : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      <div className={`text-sm font-bold capitalize ${severity === sev ? severityColors[sev].text : "text-white/60"}`}>
                        {sev}
                      </div>
                    </button>
                  ))}
                </div>
                <div className="mt-2 text-xs text-white/40">
                  {severity === "critical" && "App is unusable or data loss"}
                  {severity === "high" && "Major feature broken"}
                  {severity === "medium" && "Feature works but has issues"}
                  {severity === "low" && "Minor cosmetic issue"}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-white/80 mb-3">
                  Category
                </label>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {(["ui", "functionality", "performance", "data", "other"] as Category[]).map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setCategory(cat)}
                      className={`rounded-lg border-2 p-4 transition-all ${
                        category === cat
                          ? "border-cyan-500/50 bg-cyan-500/20"
                          : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      <div className={`text-xs font-bold capitalize ${category === cat ? "text-cyan-200" : "text-white/60"}`}>
                        {cat === "ui" ? "UI/UX" : cat}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary Preview */}
              <div className="rounded-xl border border-white/10 bg-black/30 p-5 space-y-3">
                <h4 className="text-sm font-bold text-white/80">Bug Report Summary</h4>
                <div className="text-xs text-white/60 space-y-2">
                  <div><span className="text-white/40">Title:</span> {title || "(not set)"}</div>
                  <div><span className="text-white/40">Severity:</span> <span className="capitalize">{severity}</span></div>
                  <div><span className="text-white/40">Category:</span> <span className="capitalize">{category}</span></div>
                  <div><span className="text-white/40">Screenshot:</span> {screenshot ? "Yes" : "No"}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-white/10 bg-black/20 p-6 flex justify-between">
          <button
            onClick={currentStep > 1 ? () => setCurrentStep(currentStep - 1) : handleClose}
            disabled={isSubmitting}
            className="px-6 py-3 rounded-lg border border-white/10 bg-white/5 text-sm font-bold text-white hover:bg-white/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {currentStep > 1 ? "Back" : "Cancel"}
          </button>

          <button
            onClick={currentStep < 3 ? () => setCurrentStep(currentStep + 1) : handleSubmit}
            disabled={isSubmitting || (!title.trim() || !description.trim())}
            className="px-8 py-3 rounded-lg border border-red-500/50 bg-gradient-to-r from-red-500/30 to-red-600/30 text-sm font-bold text-white hover:from-red-500/40 hover:to-red-600/40 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Submitting...
              </span>
            ) : currentStep < 3 ? (
              "Next"
            ) : (
              "Submit Bug Report"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
